import { formatBooleanForCSV, formatDateForCSV } from "./core";
import type { CSVExportOptions } from "./core";
import type { CRMRelationOverlay } from "@/lib/types/table/entity-relation-types";

/**
 * Type definition for grouped items.
 * Extends CRMRelationOverlay so grouping metadata fields (groupLabel, groupPath,
 * groupKeys, level) are typed rather than accessed via `(item as any)`.
 */
export interface GroupedItem extends Partial<CRMRelationOverlay> {
  type: "group-header" | "data";
  groupKey?: string;
  groupLabel?: string;
  groupValue?: string;
  itemCount?: number;
  id?: string;
  [key: string]: unknown;
}

/** Narrows an unknown grouped-row cell to the input the CSV date helpers accept. */
function toDateValue(value: unknown): string | Date {
  return value instanceof Date ? value : String(value);
}

/**
 * Converts grouped data to CSV format with group headers
 */
export function groupedDataToCSV(
  groupedData: GroupedItem[],
  visibleFields: Array<[string, { label: string; type: string }]>,
  options: CSVExportOptions = {}
): string {
  if (!groupedData || groupedData.length === 0) {
    return "";
  }

  const { includeHeaders = true } = options;
  const csvRows: string[] = [];

  // Add headers if requested
  if (includeHeaders) {
    const headers = visibleFields.map(([_, config]) => config.label);
    csvRows.push(headers.map((h) => `"${h}"`).join(","));
  }

  // Process grouped data (supports dual/nested grouping)
  groupedData.forEach((item) => {
    if (item.type === "group-header") {
      // Add group header row with proper indentation for dual grouping
      const level = item.level || 1;
      const indentation = "  ".repeat(level - 1); // Indent secondary groups
      const groupLabel = `${indentation}${item.groupLabel} (${item.itemCount} items)`;
      const emptyColumns = Array(visibleFields.length - 1)
        .fill("\"\"")
        .join(",");
      csvRows.push(`"${groupLabel}",${emptyColumns}`);
    } else if (item.type === "data") {
      // Add data row
      const values = visibleFields.map(([fieldId, fieldConfig]) => {
        const value = item[fieldId];

        if (value === null || value === undefined) {
          return "\"\"";
        }

        switch (fieldConfig.type) {
          case "date":
            return `"${formatDateForCSV(toDateValue(value))}"`;
          case "datetime":
            return `"${value ? new Date(toDateValue(value)).toISOString() : ""}"`;
          case "boolean":
            return `"${formatBooleanForCSV(Boolean(value))}"`;
          default: {
            const stringValue = String(value).replace(/"/g, "\"\"");
            return `"${stringValue}"`;
          }
        }
      });

      csvRows.push(values.join(","));
    }
  });

  return csvRows.join("\n");
}

/**
 * Converts grouped data to structured CSV format (analysis-friendly)
 * Adds group information as columns instead of header rows
 */
export function groupedDataToStructuredCSV(
  groupedData: GroupedItem[],
  visibleFields: Array<[string, { label: string; type: string }]>,
  groupingConfig?: {
    primaryField?: string;
    secondaryField?: string;
    primaryLabel?: string;
    secondaryLabel?: string;
  },
  options: CSVExportOptions = {}
): string {
  if (!groupedData || groupedData.length === 0) {
    return "";
  }

  const { includeHeaders = true } = options;
  const csvRows: string[] = [];

  // Extract only data items (filter out group headers)
  const dataItems = groupedData.filter((item) => item.type === "data");

  if (dataItems.length === 0) {
    return "";
  }

  // Build headers with group columns first
  const headers: string[] = [];

  // Add group columns
  if (groupingConfig?.primaryField) {
    headers.push(groupingConfig.primaryLabel || `${groupingConfig.primaryField}_group`);
  }
  if (groupingConfig?.secondaryField) {
    headers.push(groupingConfig.secondaryLabel || `${groupingConfig.secondaryField}_group`);
  }

  // Add regular field headers
  visibleFields.forEach(([_, config]) => {
    headers.push(config.label);
  });

  // Add headers if requested
  if (includeHeaders) {
    csvRows.push(headers.map((h) => `"${h}"`).join(","));
  }

  // Process data items
  dataItems.forEach((item) => {
    const values: string[] = [];

    // Add group values with better null handling
    // Use groupLabel from item if available (more user-friendly), otherwise fall back to groupKey
    if (groupingConfig?.primaryField) {
      let primaryGroupValue = "";

      // Try to get the friendly group label from groupPath or groupLabel
      if (item.groupPath && item.groupPath[0]) {
        primaryGroupValue = item.groupPath[0];
      } else if (item.groupLabel) {
        primaryGroupValue = item.groupLabel;
      } else if (item.groupKeys && item.groupKeys[0] !== undefined && item.groupKeys[0] !== null) {
        primaryGroupValue = String(item.groupKeys[0]);
      }

      // Provide user-friendly labels for empty/null group values
      if (
        !primaryGroupValue ||
        primaryGroupValue === "no-value" ||
        primaryGroupValue === "null" ||
        primaryGroupValue === "no-date"
      ) {
        primaryGroupValue = "(No Value)";
      }
      values.push(`"${primaryGroupValue}"`);
    }
    if (groupingConfig?.secondaryField) {
      let secondaryGroupValue = "";

      // Try to get the friendly group label from groupPath
      if (item.groupPath && item.groupPath.length > 1 && item.groupPath[1]) {
        secondaryGroupValue = item.groupPath[1];
      } else if (
        item.groupKeys &&
        item.groupKeys.length > 1 &&
        item.groupKeys[1] !== undefined &&
        item.groupKeys[1] !== null
      ) {
        secondaryGroupValue = String(item.groupKeys[1]);
      }

      // Provide user-friendly labels for empty/null group values
      if (
        !secondaryGroupValue ||
        secondaryGroupValue === "no-value" ||
        secondaryGroupValue === "null" ||
        secondaryGroupValue === "no-date"
      ) {
        secondaryGroupValue = "(No Value)";
      }
      values.push(`"${secondaryGroupValue}"`);
    }

    // Add regular field values
    visibleFields.forEach(([fieldId, fieldConfig]) => {
      const value = item[fieldId];

      if (value === null || value === undefined) {
        values.push("\"\"");
        return;
      }

      switch (fieldConfig.type) {
        case "date":
          values.push(`"${formatDateForCSV(toDateValue(value))}"`);
          break;
        case "datetime":
          values.push(`"${value ? new Date(toDateValue(value)).toISOString() : ""}"`);
          break;
        case "boolean":
          values.push(`"${formatBooleanForCSV(Boolean(value))}"`);
          break;
        default: {
          const stringValue = String(value).replace(/"/g, "\"\"");
          values.push(`"${stringValue}"`);
        }
      }
    });

    csvRows.push(values.join(","));
  });

  return csvRows.join("\n");
}
